import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { User } from '@shared/types'
import { ApiError, alPerderSesion, api, guardarToken, leerToken } from './api'

interface SessionValue {
  user: User | null
  /** true mientras se resuelve la sesión inicial: evita parpadeos de "entrar". */
  cargando: boolean
  entrar(email: string, password: string): Promise<void>
  registrar(name: string, email: string, password: string): Promise<void>
  salir(): Promise<void>
}

const SessionContext = createContext<SessionValue | null>(null)

interface RespuestaSesion {
  user: User
  token: string
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [cargando, setCargando] = useState(true)

  // Si el servidor rechaza la sesión a media navegación, lib/api ya borró el
  // token vencido: aquí solo queda dejar al usuario en null para que los guardas
  // de rutas lleven a /entrar en vez de dejar la pantalla en un error sin salida.
  useEffect(() => alPerderSesion(() => setUser(null)), [])

  // Al arrancar, si hay token guardado se valida contra el servidor. Un token
  // vencido se descarta en silencio en vez de dejar la app en un estado raro.
  useEffect(() => {
    let vigente = true

    async function restaurar() {
      if (!leerToken()) {
        if (vigente) setCargando(false)
        return
      }
      try {
        const { user: actual } = await api.get<{ user: User }>('/api/auth/yo')
        if (vigente) setUser(actual)
      } catch (error) {
        if (error instanceof ApiError && error.esNoAutenticado) guardarToken(null)
      } finally {
        if (vigente) setCargando(false)
      }
    }

    void restaurar()
    return () => {
      vigente = false
    }
  }, [])

  const entrar = useCallback(async (email: string, password: string) => {
    const datos = await api.post<RespuestaSesion>('/api/auth/entrar', { email, password })
    guardarToken(datos.token)
    setUser(datos.user)
  }, [])

  const registrar = useCallback(async (name: string, email: string, password: string) => {
    const datos = await api.post<RespuestaSesion>('/api/auth/registro', { name, email, password })
    guardarToken(datos.token)
    setUser(datos.user)
  }, [])

  const salir = useCallback(async () => {
    try {
      await api.post('/api/auth/salir')
    } finally {
      guardarToken(null)
      setUser(null)
    }
  }, [])

  const valor = useMemo<SessionValue>(
    () => ({ user, cargando, entrar, registrar, salir }),
    [user, cargando, entrar, registrar, salir],
  )

  return <SessionContext.Provider value={valor}>{children}</SessionContext.Provider>
}

export function useSession(): SessionValue {
  const valor = useContext(SessionContext)
  if (!valor) throw new Error('useSession() fuera de <SessionProvider>')
  return valor
}
